import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { classi } from '../../accesso/campi';
import { titoloImmobile, daCompilare } from '../../../utils/percorso';
import { ensureAbsoluteUrl } from '../../../utils/api';
import {
  formatEuroMq, statoAgenzia, testoAgenzia, provenienza, fotoImmobile, testoPrezzo,
} from '../../../utils/immobili';
import { useTraduzioni } from '../../../i18n';
import { DecisioneCard } from './Decisione';
import { DatiImmobile, PilloleDintorni } from './DatoImmobile';
import RigaVisitaCliente from '../RigaVisitaCliente';

// Card di un immobile nell'elenco (bozza design/immobili-v3.html): foto con prezzo ed €/m², un'etichetta di
// stato, pallino dell'agenzia, dati, dintorni, frase dello staff, decisione se è da decidere, piede.

// Una foto che non si carica lascia il posto alla successiva, come nella galleria della scheda (prima la card restava
// senza foto se la prima non si caricava, anche con altre foto buone)
export const Foto = ({ immobile }) => {
  const [rotte, setRotte] = useState([]);
  const url = fotoImmobile(immobile).find((u) => !rotte.includes(u));
  return url ? <img key={url} src={url} alt="" loading="lazy" onError={() => setRotte((r) => (r.includes(url) ? r : [...r, url]))} /> : null;
};

// onRifiuto(messaggio): il server ha rifiutato una risposta ("Volete vederla?", "Sposta") perché la casa o la visita
// è cambiata nel frattempo: la pagina rilegge l'elenco e lo dice (prima l'elenco restava vecchio e si riprovava
// all'infinito). visite: l'elenco completo, per la riga della visita.
const CardImmobile = ({ voce, consulente, adesso, onRisposta, onRifiuto, selezione, visite = null }) => {
  const { t, l } = useTraduzioni();
  const { immobile, etichetta, prezzo, prezzoMq, daDecidere } = voce;
  const { search } = useLocation();
  const compilare = daCompilare(immobile);
  const agenzia = statoAgenzia(immobile);
  const scheda = `/immobili/${encodeURIComponent(immobile.id)}`;
  // la scheda sa da quale elenco (filtro e ordinamento) si arriva: "Tutti gli immobili" ci riporta lì
  const daElenco = { daElenco: search || '' };
  const nonOnline = !!immobile.annuncio_non_online && etichetta.classe !== 'scartato';

  return (
    <article className={classi('pc', etichetta.classe === 'scartato' && 'out', selezione?.scelta && 'scelta')} data-testid="card-immobile">
      <Link className="im" to={scheda} state={daElenco} aria-label={titoloImmobile(immobile, l)}>
        <Foto immobile={immobile} />
        <span className={`tag t-${etichetta.classe}`}>{etichetta.testo}</span>
        {agenzia && !selezione && (
          <span className={`ag ${agenzia}`} title={`${immobile.agenzia_info?.nome || immobile.agenzia || t('immobili.scheda.agenzia')} · ${testoAgenzia(agenzia, l).toLowerCase()}`}>
            <i />
          </span>
        )}
        {/* senza prezzo niente "—" isolato: le stesse parole della scheda e della home ("Prezzo in arrivo" finché mancano i dati) */}
        <span className={classi('pr', !prezzo && 'muto', !prezzo && 'da-verificare')} data-testid="prezzo-card">
          {testoPrezzo(immobile, l)}
          {prezzoMq && <small>{formatEuroMq(prezzoMq, l)}</small>}
        </span>
      </Link>
      {selezione && (
        <button type="button" className={classi('seleziona', selezione.scelta && 'on')} aria-pressed={selezione.scelta} onClick={selezione.alterna}>
          {t(selezione.scelta ? 'immobili.confronto.scelta' : 'immobili.confronto.scegli')}
        </button>
      )}
      <div className="bd">
        <h4 className={compilare ? 'muto' : undefined}><Link to={scheda} state={daElenco}>{titoloImmobile(immobile, l)}</Link></h4>
        {compilare ? (
          <div className="meta"><span>{t('immobili.card.dati_in_arrivo')}</span></div>
        ) : <DatiImmobile immobile={immobile} quanti={4} piccoli />}
        <PilloleDintorni immobile={immobile} quante={4} />
        {immobile.parere_breve && (
          <div className="frase">“{immobile.parere_breve}”{immobile.parere_autore_nome ? ` — ${immobile.parere_autore_nome}` : ''}</div>
        )}
        {/* una casa tolta dalla ricerca non ha una richiesta in corso (prima "Scartato" con sotto "Avete chiesto la visita") */}
        {immobile.visita_richiesta && etichetta.classe !== 'scartato' && (
          <div className="pending">{t('immobili.card.visita_richiesta', { nome: consulente?.nome || t('comune.il_consulente') })}</div>
        )}
        {nonOnline && (
          <div className="fuori-portale" data-testid="annuncio-non-online">{t('immobili.card.non_online')}</div>
        )}
        {/* adesso: "oggi · 04:00" / "domani · 10:30" come l'etichetta sulla foto (difetto 24) */}
        {etichetta.classe !== 'scartato' && !selezione && (
          <RigaVisitaCliente immobile={immobile} consulente={consulente} onFatto={onRisposta} onRifiuto={onRifiuto} adesso={adesso} visite={visite} />
        )}
        {/* annuncio tolto dal portale: niente "Volete vederla?" finché l'agenzia non conferma */}
        {/* onRisposta(scelta, immobile): se la casa esce dall'elenco la pagina conferma nel suo avviso */}
        {daDecidere && !nonOnline && !selezione && (
          <DecisioneCard immobile={immobile} consulente={consulente} onFatto={(scelta) => onRisposta(scelta, immobile)} onRifiuto={onRifiuto} />
        )}
        <div className="ft">
          <span>{provenienza(immobile, adesso, l)}</span>
          {compilare
            ? <a className="link" href={ensureAbsoluteUrl(immobile.link)} target="_blank" rel="noopener noreferrer">{t('immobili.card.annuncio')}</a>
            : <Link className="link" to={scheda} state={daElenco}>{t('immobili.card.apri')}</Link>}
        </div>
      </div>
    </article>
  );
};

export default CardImmobile;
